import { customHeaders, requestApi } from 'api/api'
import {
  CreateDistributionRuleRequest,
  CreatePortfolioRequest,
  CustomerEarningsSummaryParams,
  CustomerEarningsSummaryResponse,
  DepositRequest,
  DepositTransferResponse,
  DistributionRequest,
  EarningsDistributionResponse,
  GetMonthlyCustomersEarningsParams,
  GetMonthlyCustomersEarningsResponse,
  GetPortfoliosParams,
  GetPortfoliosResponse,
  GetRewardsProductsParams,
  GetRewardsProductsResponse,
  MultiFactorAuthorizationRequest,
  Portfolio,
  RewardsProduct,
  WithdrawalRequest,
  WithdrawalTransferResponse,
} from 'types/rewards'

export const createPortfolio = async (data: CreatePortfolioRequest): Promise<Portfolio> => {
  return requestApi({
    url: '/earn/v1/portfolios',
    baseUrl: process.env.API_URL,
    method: 'POST',
    data,
  })
}

export const getPortfolios = async ({
  pageNumber,
  pageSize,
}: GetPortfoliosParams): Promise<GetPortfoliosResponse> => {
  return requestApi({
    url: '/earn/v1/portfolios',
    baseUrl: process.env.API_URL,
    method: 'GET',
    params: {
      ...(pageNumber && { pageNumber }),
      ...(pageSize && { pageSize }),
    },
  })
}

export const getPortfolio = async (portfolioId: string): Promise<Portfolio> => {
  return requestApi({
    url: `/earn/v1/portfolios/${portfolioId}`,
    baseUrl: process.env.API_URL,
    method: 'GET',
  })
}

export const depositToPortfolio = async (
  data: DepositRequest
): Promise<DepositTransferResponse> => {
  return requestApi({
    url: '/earn/v1/deposits',
    baseUrl: process.env.API_URL,
    method: 'POST',
    data,
    headers: { ...customHeaders, 'Idempotency-Key': data.reference },
  })
}

export const withdrawFromPortfolio = async (
  data: WithdrawalRequest
): Promise<WithdrawalTransferResponse> => {
  return requestApi({
    url: '/earn/v1/withdrawals',
    baseUrl: process.env.API_URL,
    method: 'POST',
    data,
    headers: { ...customHeaders, 'Idempotency-Key': data.reference },
  })
}

export const getRewardsProducts = async ({
  pageSize,
  pageNumber,
  currency,
}: GetRewardsProductsParams): Promise<GetRewardsProductsResponse> => {
  return requestApi({
    url: '/earn/v1/products',
    baseUrl: process.env.API_URL,
    method: 'GET',
    params: {
      ...(pageSize && { pageSize }),
      ...(pageNumber && { pageNumber }),
      ...(currency && { currency }),
    },
  })
}

export const getRewardsProduct = async (rewardsProductId: string): Promise<RewardsProduct> => {
  return requestApi({
    url: `/earn/v1/products/${rewardsProductId}`,
    baseUrl: process.env.API_URL,
    method: 'GET',
  })
}

export const getCustomerEarningsSummary = async ({
  currency,
  type,
}: CustomerEarningsSummaryParams): Promise<CustomerEarningsSummaryResponse> => {
  return requestApi({
    url: '/earn/v1/portfolios/summary',
    baseUrl: process.env.API_URL,
    method: 'GET',
    params: {
      q: `currency:${currency} AND type:${type}`,
    },
  })
}

export const getMonthlyCustomersEarnings = async ({
  currency,
  startDate,
  endDate,
  pageNumber,
  pageSize,
}: GetMonthlyCustomersEarningsParams): Promise<GetMonthlyCustomersEarningsResponse> => {
  return requestApi({
    url: '/earn/v1/monthly-aggregates',
    baseUrl: process.env.API_URL,
    method: 'GET',
    params: {
      currency,
      ...(startDate && { startDate }),
      ...(endDate && { endDate }),
      ...(pageNumber !== undefined && { pageNumber }),
      ...(pageSize && { pageSize }),
    },
  })
}

export const initiateCustomersRewardsDistribution = async (
  data: DistributionRequest
): Promise<void> => {
  return requestApi({
    url: '/earn/v1/distributions',
    baseUrl: process.env.API_URL,
    method: 'POST',
    data,
  })
}

export const getEarningsDistribution = async (
  currency: string
): Promise<EarningsDistributionResponse> => {
  return requestApi({
    url: '/earn/v1/distribution-rules',
    baseUrl: process.env.API_URL,
    method: 'GET',
    params: {
      currency,
    },
  })
}

export const createDistributionRule = async (
  data: CreateDistributionRuleRequest
): Promise<void> => {
  return requestApi({
    url: '/earn/v1/distribution-rules',
    baseUrl: process.env.API_URL,
    method: 'POST',
    data,
  })
}

export const authorizeWithdrawal = async (
  data: MultiFactorAuthorizationRequest
): Promise<unknown> => {
  return requestApi({
    url: '/earn/v1/withdrawals-authorisations',
    baseUrl: process.env.API_URL,
    method: 'POST',
    data,
  })
}
